import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, transferArrayItem, moveItemInArray } from '@angular/cdk/drag-drop';
import { GameModeService } from '../../../_services/game-mode.service';
import { MiniGameService } from '../../../_services/mini-game.service';
import { ClassStudentsService } from '../../../_services/class-students.service';
import { MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { GamePhase } from '../../../interfaces/game-state.interface';
import { GlobalService } from 'src/app/_services/global.service';

@Component({
  selector: 'app-tug-of-war-team-setup',
  templateUrl: './tug-of-war-team-setup.component.html',
  styleUrls: ['./tug-of-war-team-setup.component.scss']
})
export class TugOfWarTeamSetupComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  availableStudents: MiniGameStudent[] = [];
  teamAStudents: MiniGameStudent[] = [];
  teamBStudents: MiniGameStudent[] = [];

  teamAName = { en: 'Blue Team', ar: 'الفريق الأزرق' };
  teamBName = { en: 'Red Team', ar: 'الفريق الأحمر' };

  teamANameInput = 'Blue Team';
  teamBNameInput = 'Red Team';

  constructor(
    private gameModeService: GameModeService,
    private miniGameService: MiniGameService,
    private classStudentsService: ClassStudentsService,
    private globalService: GlobalService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currLang = lang;
        this.teamANameInput = this.currLang === 'ar' ? this.teamAName.ar : this.teamAName.en;
        this.teamBNameInput = this.currLang === 'ar' ? this.teamBName.ar : this.teamBName.en;
      });

    const classId = this.gameModeService.getCurrentClassId();
    let students = classId ? this.classStudentsService.getStudentsByClass(classId) : [];
    if (!students.length) {
      students = this.classStudentsService.getAllStudents();
    }
    this.availableStudents = [...students];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTeamANameChange(value: string): void {
    this.teamANameInput = value;
    if (this.currLang === 'ar') {
      this.teamAName.ar = value;
    } else {
      this.teamAName.en = value;
    }
  }

  onTeamBNameChange(value: string): void {
    this.teamBNameInput = value;
    if (this.currLang === 'ar') {
      this.teamBName.ar = value;
    } else {
      this.teamBName.en = value;
    }
  }

  drop(event: CdkDragDrop<MiniGameStudent[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }
  }

  randomizeTeams(): void {
    // Gather all students from all three lists
    const all = [...this.availableStudents, ...this.teamAStudents, ...this.teamBStudents];
    // Shuffle
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    const mid = Math.ceil(all.length / 2);
    this.teamAStudents = all.slice(0, mid);
    this.teamBStudents = all.slice(mid);
    this.availableStudents = [];
  }

  canFinalize(): boolean {
    return this.teamAStudents.length >= 1 && this.teamBStudents.length >= 1;
  }

  finalizeTeams(): void {
    if (!this.canFinalize()) return;

    const teamAStudentsTagged = this.teamAStudents.map(s => ({ ...s, teamId: 'A' as const }));
    const teamBStudentsTagged = this.teamBStudents.map(s => ({ ...s, teamId: 'B' as const }));

    const teams = {
      teamA: {
        id: 'A' as const,
        name: { ...this.teamAName },
        color: '#2196F3',
        students: teamAStudentsTagged,
        score: 0
      },
      teamB: {
        id: 'B' as const,
        name: { ...this.teamBName },
        color: '#F44336',
        students: teamBStudentsTagged,
        score: 0
      }
    };

    const allStudents = [...teamAStudentsTagged, ...teamBStudentsTagged];
    this.miniGameService.startTugOfWarWithTeams(teams, allStudents);
  }

  goBack(): void {
    this.gameModeService.setPhase(GamePhase.GAME_SELECT);
  }

  getStudentName(student: MiniGameStudent): string {
    return this.currLang === 'ar' ? student.name.ar : student.name.en;
  }
}
